/**
 * Report presenters (UI phase D, ADR 0031): the summary figures and the primary chart of each report,
 * built from the rows the report RPC returned (screen) or the export call returned (PDF). Shared by the
 * screens and their PDF exports so both always show the same numbers.
 */
import { drivingBandLabels, type ChecklistComplianceRow, type DrivingResult, type EventRow, type UsageRow } from "@fts/core";
import type { TFn } from "../../i18n";
import type { ChartBarSpec, ChartSpec } from "../../lib/chart";
import type { SummaryLine } from "../../lib/export/sheet";
import {
  checklistTotals,
  complianceByVehicle,
  drivingBandBars,
  eventTotals,
  eventsByDay,
  formatInt,
  formatNum,
  formatPct,
  trendOf,
  usageByDay,
  usageByVehicle,
  usageTotals,
  type ChartBar,
} from "../../lib/report-figures";
import { drivingRowLabel, durationLabel } from "../../lib/report-sheets";
import type { SummaryItem } from "./ReportParts";
import { trendText } from "./ReportParts";

export type Ctx = { t: TFn; locale: string; lang: "tr" | "en" };

/** Short axis label of a day ("27.10" / "27/10") and its long form for tooltips. */
function dayLabels(bars: ChartBar[], ctx: Ctx): ChartBarSpec[] {
  return bars.map((b) => {
    const [y, m, d] = b.key.split("-");
    const date = new Date(`${b.key}T12:00:00Z`);
    return {
      ...b,
      label: `${d}${ctx.lang === "tr" ? "." : "/"}${m}`,
      title: Number.isNaN(date.getTime()) ? b.key : date.toLocaleDateString(ctx.locale, { weekday: "short", day: "numeric", month: "long", year: y ? "numeric" : undefined, timeZone: "UTC" }),
    };
  });
}

/** Summary items as PDF lines (the comparison becomes the note). */
export function toSummaryLines(ctx: Ctx, items: SummaryItem[]): SummaryLine[] {
  return items.map((i) => ({ label: i.label, value: i.value, note: trendText(ctx.t, ctx.locale, i.trend) }));
}

/* ---------------- usage ---------------- */

export function usageSummary(ctx: Ctx, rows: readonly UsageRow[], prev: readonly UsageRow[] | null): SummaryItem[] {
  const { t, locale } = ctx;
  const c = usageTotals(rows);
  const p = prev ? usageTotals(prev) : null;
  const km = (v: number) => t("reports.km", { km: formatNum(v, locale, 1) });
  return [
    { label: t("reports.usage.totalIgnition"), value: durationLabel(t, c.ignitionS), trend: p && trendOf(c.ignitionS, p.ignitionS), previous: p && durationLabel(t, p.ignitionS) },
    { label: t("reports.usage.totalMoving"), value: durationLabel(t, c.movingS), trend: p && trendOf(c.movingS, p.movingS), previous: p && durationLabel(t, p.movingS) },
    { label: t("reports.usage.totalDistance"), value: km(c.distanceKm), trend: p && trendOf(c.distanceKm, p.distanceKm), previous: p && km(p.distanceKm) },
    {
      label: t("reports.usage.avgUtilisation"),
      value: c.utilisation === null ? t("common.empty") : formatPct(c.utilisation, locale),
      trend: p && trendOf(c.utilisation, p.utilisation, "pts"),
      previous: p && (p.utilisation === null ? t("common.empty") : formatPct(p.utilisation, locale)),
    },
    { label: t("reports.usage.vehiclesUsed"), value: formatInt(c.vehicles, locale), trend: p && trendOf(c.vehicles, p.vehicles), previous: p && formatInt(p.vehicles, locale) },
  ];
}

export type UsageChartBy = "day" | "vehicle";

export function usageChart(ctx: Ctx, rows: readonly UsageRow[], by: UsageChartBy, from: string, to: string): ChartSpec {
  const { t, locale } = ctx;
  const c = usageTotals(rows);
  const bars = by === "day" ? dayLabels(usageByDay(rows, from, to), ctx) : usageByVehicle(rows);
  return {
    orientation: by === "day" ? "vertical" : "horizontal",
    series: [
      { label: t("reports.usage.series.moving"), color: "accent" },
      { label: t("reports.usage.series.idle"), color: "ink" },
      { label: t("reports.usage.series.off"), color: "muted" },
    ],
    bars,
    unit: t("reports.unit.hours"),
    digits: 1,
    locale,
    // No stacked "total": moving + idle are over the whole day, the third part only within the basis,
    // so their sum is not a meaningful number.
    categoryLabel: by === "day" ? t("reports.col.day") : t("reports.col.vehicle"),
    summary: t("reports.usage.chart.summary", { moving: durationLabel(t, c.movingS), idle: durationLabel(t, c.idleS), off: durationLabel(t, c.offInBasisS) }),
  };
}

/* ---------------- events ---------------- */

export function eventSummary(ctx: Ctx, rows: readonly EventRow[], prev: readonly EventRow[] | null): SummaryItem[] {
  const { t, locale } = ctx;
  const c = eventTotals(rows);
  const p = prev ? eventTotals(prev) : null;
  const item = (label: string, k: keyof typeof c): SummaryItem => ({ label, value: formatInt(c[k], locale), trend: p && trendOf(c[k], p[k]), previous: p && formatInt(p[k], locale) });
  return [
    item(t("reports.events.total"), "total"),
    item(t("reports.events.impacts"), "impacts"),
    item(t("reports.events.severe"), "severe"),
    // Open alarms: a state now, not a count of the period — no comparison.
    { label: t("reports.events.open"), value: formatInt(c.open, locale) },
    item(t("reports.events.offHours"), "offHours"),
  ];
}

export function eventChart(ctx: Ctx, rows: readonly EventRow[], from: string, to: string, tz: string): ChartSpec {
  const { t, locale } = ctx;
  const c = eventTotals(rows);
  return {
    orientation: "vertical",
    series: [
      { label: t("reports.events.series.impact", { level: 1 }), color: "ramp1" },
      { label: t("reports.events.series.impact", { level: 2 }), color: "ramp3" },
      { label: t("reports.events.series.impact", { level: 3 }), color: "crit" },
      { label: t("reports.events.series.other"), color: "ink" },
    ],
    bars: dayLabels(eventsByDay(rows, from, to, tz), ctx),
    digits: 0,
    locale,
    totalLabel: t("reports.chart.total"),
    categoryLabel: t("reports.col.day"),
    summary: t("reports.events.chart.summary", { total: formatInt(c.total, locale), impacts: formatInt(c.impacts, locale), severe: formatInt(c.severe, locale) }),
  };
}

/* ---------------- checklist ---------------- */

export function checklistSummary(ctx: Ctx, rows: readonly ChecklistComplianceRow[], prev: readonly ChecklistComplianceRow[] | null): SummaryItem[] {
  const { t, locale } = ctx;
  const c = checklistTotals(rows);
  const p = prev ? checklistTotals(prev) : null;
  const pct = (v: number | null) => (v === null ? t("common.empty") : formatPct(v, locale));
  const hours = (v: number | null) => (v === null ? t("common.empty") : t("reports.hours", { h: formatNum(v, locale, 1) }));
  const int = (label: string, k: "shiftsUsed" | "missed" | "faultsOpened"): SummaryItem => ({ label, value: formatInt(c[k], locale), trend: p && trendOf(c[k], p[k]), previous: p && formatInt(p[k], locale) });
  return [
    { label: t("reports.checklist.totalCompliance"), value: pct(c.compliance), trend: p && trendOf(c.compliance, p.compliance, "pts"), previous: p && pct(p.compliance) },
    int(t("reports.col.shiftsUsed"), "shiftsUsed"),
    int(t("reports.col.missed"), "missed"),
    int(t("reports.col.faultsOpened"), "faultsOpened"),
    { label: t("reports.checklist.mttr"), value: hours(c.mttrH), trend: p && trendOf(c.mttrH, p.mttrH), previous: p && hours(p.mttrH) },
  ];
}

export function checklistChart(ctx: Ctx, rows: readonly ChecklistComplianceRow[]): ChartSpec {
  const { t, locale } = ctx;
  const bars = complianceByVehicle(rows);
  const vals = bars.map((b) => b.values[0]!);
  return {
    orientation: "horizontal",
    series: [{ label: t("reports.checklist.chart.series"), color: "accent" }],
    bars,
    max: 100,
    percent: true,
    digits: 1,
    locale,
    categoryLabel: t("reports.col.vehicle"),
    summary: bars.length
      ? t("reports.checklist.chart.summary", { count: bars.length, min: formatPct(Math.min(...vals), locale), max: formatPct(Math.max(...vals), locale) })
      : "",
  };
}

/* ---------------- driving ---------------- */

export function drivingSummary(ctx: Ctx, res: DrivingResult, prev: DrivingResult | null): SummaryItem[] {
  const { t, locale } = ctx;
  const c = res.totals;
  const p = prev?.totals ?? null;
  const km = (m: number) => t("reports.km", { km: formatNum(m / 1000, locale, 1) });
  const kmh = (v: number | null) => (v === null ? t("common.empty") : formatNum(v, locale, 1));
  const rate = (v: number | null) => (v === null ? t("common.empty") : formatNum(v, locale, 2));
  const imp = (m: typeof c) => m.impacts[0] + m.impacts[1] + m.impacts[2];
  return [
    { label: t("reports.usage.totalMoving"), value: durationLabel(t, c.movingS), trend: p && trendOf(c.movingS, p.movingS), previous: p && durationLabel(t, p.movingS) },
    { label: t("reports.usage.totalDistance"), value: km(c.distanceM), trend: p && trendOf(c.distanceM, p.distanceM), previous: p && km(p.distanceM) },
    { label: t("reports.driving.col.avgKmh"), value: kmh(c.avgKmh), trend: p && trendOf(c.avgKmh, p.avgKmh), previous: p && kmh(p.avgKmh) },
    { label: t("reports.driving.impacts"), value: formatInt(imp(c), locale), trend: p && trendOf(imp(c), imp(p)), previous: p && formatInt(imp(p), locale) },
    {
      label: t("reports.driving.col.eventsRate", { h: res.rateHours }),
      value: rate(c.eventsPer10h),
      trend: p && trendOf(c.eventsPer10h, p.eventsPer10h),
      previous: p && rate(p.eventsPer10h),
    },
  ];
}

export function drivingChart(ctx: Ctx, res: DrivingResult): ChartSpec {
  const { t, locale } = ctx;
  const bands = drivingBandLabels(res.bandsKmh);
  const colors = ["ramp1", "ramp2", "ramp3", "ramp4"] as const;
  const bars = drivingBandBars(res, t("reports.driving.chart.all"), (r) => drivingRowLabel(t, r));
  const all = bars[0]?.key === "totals" ? bars[0].values : null;
  const top = all ? all.indexOf(Math.max(...all)) : -1;
  return {
    orientation: "horizontal",
    series: bands.map((b, i) => ({ label: t("reports.driving.col.bandShort", { band: b }), color: colors[Math.min(i, colors.length - 1)]! })),
    bars,
    max: 100,
    percent: true,
    digits: 1,
    locale,
    categoryLabel: res.group === "operator" ? t("reports.col.operator") : t("reports.col.vehicle"),
    summary: all && top >= 0 ? t("reports.driving.chart.summary", { share: formatPct(all[top]!, locale), band: t("reports.driving.col.bandShort", { band: bands[top]! }) }) : "",
  };
}
